import * as React from "react";
import { useEffect, useState } from "react";

type VideoPlayerProps = Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "src"> & {
	src?: string;
	mimeType?: string;
	useObjectUrl?: boolean;
	onSourceError?: (error: unknown) => void;
};

const isLocalObjectSource = (src: string) => src.startsWith("blob:") || src.startsWith("data:");

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
	({ src, mimeType, useObjectUrl = false, onSourceError, controls = true, ...props }, ref) => {
		const [resolvedSrc, setResolvedSrc] = useState<string>();

		useEffect(() => {
			if (!src) {
				onSourceError?.("No source provided");
				setResolvedSrc(undefined);
				return;
			}

			if (!useObjectUrl || isLocalObjectSource(src)) {
				setResolvedSrc(src);
				return;
			}

			let isCancelled = false;
			let objectUrl: string | undefined;

			setResolvedSrc(undefined);

			fetch(src)
				.then((response) => {
					if (!response.ok) throw new Error(`Failed to load video (${response.status})`);
					return response.blob();
				})
				.then((blob) => {
					if (isCancelled) return;

					const typedBlob =
						mimeType && blob.type !== mimeType
							? new Blob([blob], { type: mimeType })
							: blob;

					objectUrl = URL.createObjectURL(typedBlob);
					setResolvedSrc(objectUrl);
				})
				.catch((error) => {
					if (isCancelled) return;

					onSourceError?.(error);
					setResolvedSrc(src);
				});

			return () => {
				isCancelled = true;
				if (objectUrl) {
					URL.revokeObjectURL(objectUrl);
				}
			};
		}, [mimeType, onSourceError, src, useObjectUrl]);

		if (!resolvedSrc) return null;

		return <video ref={ref} {...props} src={resolvedSrc} controls={controls} />;
	},
);

export { VideoPlayer };
export type { VideoPlayerProps };
